import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Skeleton,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import type { Shop3CashWallet, Shop3UserSubscription, Shop3SupplierDeposit } from '@/services/shop3.service';
import type { ShopColorPalette } from '@/constants/shopColors';

interface MemberInfoCardProps {
    wallet: Shop3CashWallet | null;
    isLoading: boolean;
    colors: ShopColorPalette;
    subscription: Shop3UserSubscription | null;
    isSubscriptionLoading: boolean;
    // 예치금 관련
    deposits: Shop3SupplierDeposit[];
    currentSupplierId?: string;
    currentSupplierName?: string;
    // 캐시 페이지 연결용
    vendorPath?: string;
}

/**
 * 플랜/자산 정보 카드
 * - 구독/플랜 상태
 * - 캐시, 예치금 정보
 */
const MemberInfoCard = ({
    wallet,
    isLoading,
    colors,
    subscription,
    isSubscriptionLoading,
    deposits,
    currentSupplierId,
    currentSupplierName,
    vendorPath,
}: MemberInfoCardProps) => {
    const navigate = useNavigate();
    const [isDepositDialogOpen, setIsDepositDialogOpen] = useState(false);

    const formatNumber = (num: number) => {
        return num.toLocaleString();
    };

    // 플랜 정보 텍스트 생성
    const getPlanInfoText = () => {
        if (!subscription?.plan) {
            return '플랜 정보 없음';
        }
        const planName = subscription.plan.name;
        const sourcingFee = formatNumber(Number(subscription.plan.sourcingFee));
        return `${planName} · 사입비 ${sourcingFee}원 · 중계수수료 무료`;
    };

    // 현재 도매처 예치금
    const currentDeposit = currentSupplierId
        ? deposits.find((d) => d.supplierId === currentSupplierId)?.amount || 0
        : 0;

    // 전체 예치금 합계
    const totalDeposit = deposits.reduce((sum, d) => sum + Number(d.amount), 0);

    // 플랜 페이지로 이동
    const handlePlanClick = () => {
        if (vendorPath) {
            navigate(`/${vendorPath}/subscription/plans`);
        }
    };

    // 예치금 다이얼로그 열기
    const handleDepositClick = () => {
        setIsDepositDialogOpen(true);
    };

    return (
        <>
            <Box
                sx={{
                    bgcolor: '#fff',
                    borderRadius: 3,
                    border: '1px solid #eee',
                    mb: 2,
                    overflow: 'hidden',
                }}
            >
                {/* 플랜/구독 상태 섹션 */}
                <Box
                    onClick={handlePlanClick}
                    sx={{
                        p: { xs: 1.5, sm: 2 },
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        '&:hover': {
                            bgcolor: '#fafafa',
                        },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    {isSubscriptionLoading ? (
                        <Skeleton variant="text" width={200} height={24} />
                    ) : (
                        <Typography
                            sx={{
                                fontSize: { xs: 13, sm: 14 },
                                fontWeight: 500,
                                color: colors.main,
                            }}
                        >
                            {getPlanInfoText()}
                        </Typography>
                    )}
                    <ChevronRightIcon sx={{ fontSize: 18, color: '#999' }} />
                </Box>

                <Divider />

                {/* 자산 정보 섹션 */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1px 1fr',
                    }}
                >
                    {/* 캐시 */}
                    <Box
                        onClick={() => navigate(`/${vendorPath}/cash/charge`)}
                        sx={{
                            p: { xs: 2, sm: 2.5 },
                            display: 'flex',
                            flexDirection: 'column',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                            '&:hover': {
                                bgcolor: '#fafafa',
                            },
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <Typography sx={{ fontSize: { xs: 13, sm: 14 }, color: '#666' }}>캐시</Typography>
                            <ChevronRightIcon sx={{ fontSize: 16, color: '#999' }} />
                        </Box>
                        {isLoading ? (
                            <Skeleton variant="text" width={80} height={32} />
                        ) : (
                            <Typography
                                sx={{
                                    fontSize: { xs: 18, sm: 22 },
                                    fontWeight: 700,
                                    color: (wallet?.availableBalance ?? 0) < 0 ? '#E53935' : '#222',
                                }}
                            >
                                {formatNumber(wallet?.availableBalance ?? 0)}
                                <Typography
                                    component="span"
                                    sx={{ fontSize: { xs: 14, sm: 16 }, fontWeight: 600, color: (wallet?.availableBalance ?? 0) < 0 ? '#E53935' : '#222' }}
                                >
                                    원
                                </Typography>
                            </Typography>
                        )}
                    </Box>

                    {/* 구분선 */}
                    <Box sx={{ bgcolor: '#eee' }} />

                    {/* 예치금 */}
                    <Box
                        onClick={handleDepositClick}
                        sx={{
                            p: { xs: 2, sm: 2.5 },
                            display: 'flex',
                            flexDirection: 'column',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                            '&:hover': {
                                bgcolor: '#fafafa',
                            },
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <Typography sx={{ fontSize: { xs: 13, sm: 14 }, color: '#666' }}>예치금</Typography>
                            <ChevronRightIcon sx={{ fontSize: 16, color: '#999' }} />
                        </Box>
                        {isLoading ? (
                            <Skeleton variant="text" width={80} height={32} />
                        ) : (
                            <>
                                <Typography
                                    sx={{
                                        fontSize: { xs: 18, sm: 22 },
                                        fontWeight: 700,
                                        color: '#222',
                                    }}
                                >
                                    {formatNumber(Number(currentDeposit))}
                                    <Typography
                                        component="span"
                                        sx={{ fontSize: { xs: 14, sm: 16 }, fontWeight: 600, color: '#222' }}
                                    >
                                        원
                                    </Typography>
                                </Typography>
                                {currentSupplierName && (
                                    <Typography
                                        sx={{
                                            fontSize: { xs: 11, sm: 12 },
                                            color: '#999',
                                            mt: 0.5,
                                        }}
                                    >
                                        {currentSupplierName} 거래처에서 사용가능
                                    </Typography>
                                )}
                            </>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* 전체 예치금 목록 다이얼로그 */}
            <Dialog
                open={isDepositDialogOpen}
                onClose={() => setIsDepositDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3 },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        pb: 1,
                    }}
                >
                    <Typography sx={{ fontSize: 18, fontWeight: 700 }}>보유 예치금</Typography>
                    <IconButton onClick={() => setIsDepositDialogOpen(false)} size="small" aria-label="닫기">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 0 }}>
                    {/* 총 예치금 */}
                    <Box
                        sx={{
                            bgcolor: '#f5f5f5',
                            borderRadius: 2,
                            p: 2,
                            mb: 2,
                        }}
                    >
                        <Typography sx={{ fontSize: 13, color: '#666', mb: 0.5 }}>전체 예치금</Typography>
                        <Typography sx={{ fontSize: 24, fontWeight: 700 }}>
                            {formatNumber(totalDeposit)}원
                        </Typography>
                    </Box>

                    {/* 도매처별 예치금 목록 */}
                    {deposits.length > 0 ? (
                        <List disablePadding>
                            {[...deposits]
                                .sort((a, b) => {
                                    // 현재 접속한 쇼핑몰의 예치금을 맨 위로
                                    if (a.supplierId === currentSupplierId) return -1;
                                    if (b.supplierId === currentSupplierId) return 1;
                                    return 0;
                                })
                                .map((deposit, index) => (
                                    <ListItem
                                        key={deposit.id}
                                        sx={{
                                            px: 0,
                                            py: 1.5,
                                            borderBottom: index < deposits.length - 1 ? '1px solid #eee' : 'none',
                                        }}
                                    >
                                        <ListItemText
                                            primary={deposit.supplierName}
                                            primaryTypographyProps={{
                                                fontSize: 14,
                                                fontWeight: 400,
                                                color: '#333',
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontSize: 15,
                                                fontWeight: 600,
                                                color: '#222',
                                            }}
                                        >
                                            {formatNumber(Number(deposit.amount))}원
                                        </Typography>
                                    </ListItem>
                                ))}
                        </List>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography sx={{ fontSize: 14, color: '#999' }}>
                                보유한 예치금이 없습니다
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default MemberInfoCard;
